// CompanyCreation — company onboarding wizard launched from "Add new company".
// Three steps (info → details → features) then an "all set" screen. The final
// step submits to create-company (loginToken), then hands off to select-company.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { Button, Icon } from '@/components/common';
import { useAppDispatch } from '@/store/hooks';
import {
  useCreateCompanyMutation,
  useLazyGetOwnerDetailsQuery,
  useSelectCompanyMutation,
  type CompanyEmployeeRange,
} from '@/api/endpoints/companyApi';
import { useInitialLogoMutation } from '@/api/endpoints/mediaApi';
import { setAuth } from '@/redux/auth/authSlice';
import { uploadToPresignedUrl } from '@/utils/uploadFile';
import type { ApiErrorShape } from '@/api/types';
import CompanyCreationStepInfo from './CompanyCreationStepInfo';
import CompanyCreationStepDetails from './CompanyCreationStepDetails';
import CompanyCreationStepFeatures from './CompanyCreationStepFeatures';
import { initialCompanyCreationData, type CompanyCreationData } from './company-creation-types';
import type { FormErrors } from './validation';

const TOTAL_STEPS = 3;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CompanyCreation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [data, setData] = useState<CompanyCreationData>(initialCompanyCreationData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [companyId, setCompanyId] = useState<string | null>(null);
  // Covers the whole submit chain (logo upload + create), so the button spinner
  // and the double-submit guard span both network steps.
  const [busy, setBusy] = useState(false);

  const [createCompany] = useCreateCompanyMutation();
  const [initialLogo] = useInitialLogoMutation();
  const [selectCompany, { isLoading: entering }] = useSelectCompanyMutation();
  const [getOwnerDetails] = useLazyGetOwnerDetailsQuery();

  const patch = (next: Partial<CompanyCreationData>) => {
    setData((prev) => ({ ...prev, ...next }));
    setErrors({});
  };

  const toggleFeature = (id: string) =>
    setData((prev) => ({
      ...prev,
      features: prev.features.includes(id)
        ? prev.features.filter((f) => f !== id)
        : [...prev.features, id],
    }));

  const validateStep = (): FormErrors => {
    const next: FormErrors = {};
    if (step === 1) {
      if (!data.companyName.trim()) next.companyName = 'error_company_name_required';
      if (data.email && !EMAIL_RE.test(data.email)) next.email = 'error_invalid_email';
    } else if (step === 2) {
      if (!data.employeeRange) next.employeeRange = 'error_select_employee_range';
      if (!data.industry) next.industry = 'error_select_industry';
    }
    return next;
  };

  const goBack = () => {
    if (step === 1) navigate('/select-company');
    else setStep((s) => s - 1);
  };

  const submit = async () => {
    setBusy(true);
    try {
      // If a logo was chosen: reserve a presigned URL, PUT the file, then pass
      // the resulting mediaId to create. Any failure here aborts before create,
      // so we never leave an orphan company. No logo → skip the whole chain.
      let mediaId: string | undefined;
      if (data.logoFile) {
        const ext = data.logoFile.name.split('.').pop() || 'png';
        const { uploadUrl, mediaId: id } = await initialLogo({
          mediaType: 'image',
          imageName: `${Date.now()}.${ext}`,
          customFolderId: uuidv4(),
        }).unwrap();
        await uploadToPresignedUrl(uploadUrl, data.logoFile);
        mediaId = id;
      }

      const result = await createCompany({
        companyName: data.companyName.trim(),
        title: data.role,
        businessEmail: data.email,
        employeeRange: data.employeeRange as CompanyEmployeeRange,
        businessIndustry: data.industry,
        dashboardFeatures: JSON.stringify(data.features),
        ...(mediaId && { mediaId }),
      }).unwrap();
      setCompanyId(result.companyId);
      toast.success(t('toast_company_created'));
      setDone(true);
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    } finally {
      setBusy(false);
    }
  };

  // Done screen → open the just-created company: exchange the loginToken for a
  // company-scoped authToken (same as select-company), then enter the app.
  const enterWorkspace = async () => {
    if (entering || !companyId) return;
    try {
      const result = await selectCompany({ companyId }).unwrap();
      dispatch(
        setAuth({
          authToken: result.token,
          refreshToken: result.refreshToken,
          companyId: result.companyId,
          companyDetail: result.company,
          roles: result.roles,
          permissions: result.permissions,
          user: result.user,
          companyUserId: result.companyUserId,
          // setAuth merges — reset before the fetch below, same as useSelectCompany.
          ownerCompanyUserId: null,
        }),
      );
      try {
        const owner = await getOwnerDetails().unwrap();
        dispatch(setAuth({ ownerCompanyUserId: owner.companyUserId }));
      } catch {
        // Non-fatal: the company session is already stored; enter regardless.
      }
      navigate('/');
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  const goNext = () => {
    if (busy) return;
    const nextErrors = validateStep();
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
    else submit();
  };

  if (done) {
    return (
      <div className='company-creation company-creation--done'>
        <div className='company-creation__done'>
          <span className='company-creation__done-icon'>
            <Icon name='check' size={34} aria-hidden='true' />
          </span>
          <h1 className='company-creation__done-title'>{t('onb_done_title')}</h1>
          <p className='company-creation__done-subtitle'>{t('onb_done_subtitle')}</p>
          <Button size='lg' shape='pill' onClick={enterWorkspace} loading={entering}>
            {t('button_go_to_workspace')}
          </Button>
        </div>
      </div>
    );
  }

  const titles = ['onb_step1_title', 'onb_step2_title', 'onb_step3_title'];
  const subtitle = step === TOTAL_STEPS ? t('onb_step3_subtitle') : t('onb_complete_registration');
  const isLast = step === TOTAL_STEPS;

  return (
    <div className='company-creation'>
      <div className='company-creation__card'>
        <button type='button' className='company-creation__back' onClick={goBack}>
          <Icon
            name='arrow-left'
            size={20}
            className='company-creation__back-icon'
            aria-hidden='true'
          />
          {t('button_back')}
        </button>

        <header className='company-creation__header'>
          <h1 className='company-creation__title'>{t(titles[step - 1])}</h1>
          <p className='company-creation__subtitle'>{subtitle}</p>
          <p className='company-creation__step'>
            <span className='company-creation__step-active'>
              {t('onb_step')} {step}
            </span>{' '}
            {t('onb_of')} {TOTAL_STEPS}
          </p>
          <span className='company-creation__progress' aria-hidden='true'>
            <span
              className='company-creation__progress-fill'
              style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            />
          </span>
        </header>

        <div className='company-creation__body'>
          {step === 1 && <CompanyCreationStepInfo data={data} onChange={patch} errors={errors} />}
          {step === 2 && (
            <CompanyCreationStepDetails data={data} onChange={patch} errors={errors} />
          )}
          {step === 3 && (
            <CompanyCreationStepFeatures data={data} onToggleFeature={toggleFeature} />
          )}
        </div>

        <div className='company-creation__footer'>
          <Button size='md' shape='pill' onClick={goNext} loading={isLast && busy}>
            {isLast ? t('button_complete') : t('button_next_step')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanyCreation;
